// SummaryBand — the report summary's header card: the derived performance
// grade beside the manager's declared RAG, then the five key numbers.
//
// The grade and the RAG are deliberately adjacent and deliberately different:
// the grade is computed from records, the RAG is a person's judgement. Showing
// them together lets a reader see where the manager agrees or disagrees with
// the derivation, which is exactly the conversation a steering report exists
// to start.
//
// Contract value renders here ABBREVIATED and at full precision in the
// financial section — nowhere else on the summary. Two renderings of one
// number invite the reader to reconcile them; two is already the ceiling.

import { GradeLetter } from "../contract-overview/GradeLetter";
import { AWAITING_DATA_LABEL } from "../../../lib/contract-overview/contractGrade";
import RagIndicator from "../tracking/shared/RagIndicator";
import MetricCard from "../tracking/deliverable/MetricCard";
import ReportSection, { UnavailableNotice } from "./ReportSection";
import { countTone, formatPercent } from "./reportFormat";
import { formatAudCompact } from "../../../utils/formatCurrency";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  SectionUnavailable,
  SummaryBandSection,
} from "../../../types/report";

const SECTION_LABEL = "Executive summary";

export interface SummaryBandProps {
  section: SummaryBandSection | SectionUnavailable;
}

export default function SummaryBand({ section }: SummaryBandProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="band" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  const { grade, managerRag, managerRagSetBy, managerRagSetAt, keyNumbers } =
    section;
  const gradeLabel = grade.grade ? grade.label : AWAITING_DATA_LABEL;

  return (
    <ReportSection id="band" title={SECTION_LABEL}>
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
        <div>
          <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Performance grade
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <GradeLetter result={grade} variant="compact" />
            <span className="text-body font-medium text-slate-900 dark:text-slate-100">
              {gradeLabel}
            </span>
          </div>
        </div>
        <div>
          <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Manager rating
          </p>
          <div className="mt-1.5">
            <RagIndicator rating={managerRag} />
          </div>
          {managerRagSetBy !== null && managerRagSetAt !== null && (
            <p
              data-testid="band-rag-attribution"
              className="mt-0.5 text-caption text-slate-500 dark:text-slate-400"
            >
              Set by {managerRagSetBy}{" "}
              <span aria-hidden="true">·</span>{" "}
              {formatDayMonthYear(managerRagSetAt)}
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          testId="band-contract-value"
          label="Contract value"
          value={formatAudCompact(keyNumbers.contractValue)}
          subtitle="ex-GST"
        />
        <MetricCard
          testId="band-term-elapsed"
          label="Term elapsed"
          value={formatPercent(keyNumbers.termElapsedPct)}
        />
        <MetricCard
          testId="band-overdue"
          label="Overdue items"
          value={keyNumbers.overdueItems}
          valueTone={countTone(keyNumbers.overdueItems, "danger")}
        />
        <MetricCard
          testId="band-open-issues"
          label="Open issues"
          value={keyNumbers.openIssues}
          valueTone={countTone(keyNumbers.openIssues, "warning")}
        />
        <InvoicesInReviewCard count={keyNumbers.invoicesInReview} />
      </div>
    </ReportSection>
  );
}

/** `null` means the finance read failed, not that the queue is empty. Rendered
 *  as an em dash on the neutral tone so the gap is visible and never mistaken
 *  for a clean queue. */
function InvoicesInReviewCard({ count }: { count: number | null }) {
  if (count === null) {
    return (
      <MetricCard
        testId="band-invoices-in-review"
        label="Invoices awaiting review"
        value="—"
        subtitle="Could not be read"
      />
    );
  }
  return (
    <MetricCard
      testId="band-invoices-in-review"
      label="Invoices awaiting review"
      value={count}
      valueTone={countTone(count, "warning")}
    />
  );
}
